import React, { useEffect, useState, useContext, useRef, useMemo } from "react";
import { Form, Spinner } from "react-bootstrap";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import slugify from "slugify";
import StarekContext from "../../context/StarekContext";
import { longDateString } from "../../utils";
import {
    SidebarView,
    SidebarContext,
    SidebarContextProvider,
    useSyncedField,
} from "../../components/Sidebar";

function JobScheduleSidebarContent() {
    const starekContext = useContext(StarekContext);
    const { entity } = useContext(SidebarContext);

    const [jobName, setJobName] = useSyncedField("name", "");
    const [jobDescription, setJobDescription] = useSyncedField(
        "description",
        "",
    );
    const [selectedPart, setSelectedPart] = useSyncedField("part", "");
    const [selectedCycle, setSelectedCycle] = useSyncedField("cycle", "");
    const [selectedDevice, setSelectedDevice] = useSyncedField("device", "");
    const [dateRange, setDateRange] = useSyncedField("dateRange", [
        new Date(),
        new Date(),
    ]);

    const [jobParts, setJobParts] = useState([]);
    const [jobCycles, setJobCycles] = useState([]);
    const [jobDevices, setJobDevices] = useState([]);
    const [loadingParts, setLoadingParts] = useState(false);
    const [loadingCycles, setLoadingCycles] = useState(false);
    const [loadingDevices, setLoadingDevices] = useState(false);
    const previousDescriptionRef = useRef("");
    const previousSlugRef = useRef("");
    const userModifiedNameRef = useRef(false);
    const userModifiedDescriptionRef = useRef(false);
    const entityIdRef = useRef(null);

    const lastUsedPlant = starekContext.lastUsedPlant;
    const lastUsedWorkspace = starekContext.lastUsedWorkspace;
    const { host, scheme, port } = lastUsedWorkspace.engine;
    const token = starekContext.auth;
    const apiUrl = `${scheme}://${host}:${port}/api/v1/${lastUsedWorkspace.name}/graphql`;

    // Initialize fields from entity when it changes
    useEffect(() => {
        const currentEntityId = entity?.id || null;

        // Only reinitialize if the entity ID actually changed
        if (
            entityIdRef.current === currentEntityId &&
            currentEntityId !== null
        ) {
            return;
        }
        entityIdRef.current = currentEntityId;

        if (entity) {
            const start = new Date(entity.start_time);
            const end = new Date(entity.end_time);
            setJobName(entity.name || "");
            setJobDescription(entity.description || "");
            setSelectedPart(entity.part_id || "");
            setSelectedCycle(entity.cycle_id || "");
            setSelectedDevice(entity.device_id || "");
            setDateRange([start, end]);
            // Reset user modification flags when loading an entity
            userModifiedNameRef.current = false;
            userModifiedDescriptionRef.current = false;
        } else {
            setJobName("");
            setJobDescription("");
            setSelectedPart("");
            setSelectedCycle("");
            setSelectedDevice("");
            setDateRange([new Date(), new Date()]);
            // Reset user modification flags for new entity
            userModifiedNameRef.current = false;
            userModifiedDescriptionRef.current = false;
        }

        // Fetch all dropdown data when sidebar opens (entity changes)
        fetchDevices();
        fetchParts();
        fetchCycles();
    }, [entity?.id]);

    const fetchParts = async () => {
        if (loadingParts) return;
        setLoadingParts(true);
        try {
            const query = {
                query: `
        {
          parts(where: { plant_id: { equals: ${lastUsedPlant.id} } }) {
            id
            name
            plant_id
          }
        }
      `,
            };
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(query),
            });
            const result = await response.json();
            setJobParts(result.data?.parts || []);
        } catch (error) {
            console.error("Error fetching job parts:", error);
            setJobParts([]);
        } finally {
            setLoadingParts(false);
        }
    };

    // Auto-generate description when any field changes (but not if user manually edited or editing existing entity)
    useEffect(() => {
        if (
            !entity?.id &&
            !userModifiedDescriptionRef.current &&
            jobDevices.length > 0 &&
            jobParts.length > 0 &&
            jobCycles.length > 0
        ) {
            const selectedDeviceObj = jobDevices.find(
                (d) => d.id === selectedDevice,
            );
            const selectedPartObj = jobParts.find(
                (p) => String(p.id) === String(selectedPart),
            );
            const selectedCycleObj = jobCycles.find(
                (c) => String(c.id) === String(selectedCycle),
            );
            const [start, end] = dateRange;

            let generatedDescription = "";

            if (selectedDeviceObj) {
                generatedDescription = `${selectedDeviceObj.id}`;
            }

            if (selectedPartObj) {
                generatedDescription += ` recording ${selectedPartObj.name}`;
            }

            if (selectedCycleObj) {
                generatedDescription += ` on cycle ${selectedCycleObj.name}`;
            }

            if (start && end) {
                const startDate = longDateString(start);
                const endDate = longDateString(end);
                generatedDescription += ` between ${startDate} and ${endDate}`;
            }

            // Only update if description actually changed to avoid infinite loops
            if (generatedDescription !== previousDescriptionRef.current) {
                previousDescriptionRef.current = generatedDescription;
                setJobDescription(generatedDescription);
            }
        }
    }, [
        selectedDevice,
        selectedPart,
        selectedCycle,
        dateRange,
        entity?.id,
        jobDevices,
        jobParts,
        jobCycles,
    ]);

    // Simple hash function for dates
    const hashDates = (start, end) => {
        if (!start || !end) return "";
        const str = `${start.toISOString()}-${end.toISOString()}`;
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash = hash & hash; // Convert to 32bit integer
        }
        return Math.abs(hash).toString(36).substring(0, 8);
    };

    // Auto-generate slug name when any field changes (but not if user manually edited or editing existing entity)
    useEffect(() => {
        if (
            !entity?.id &&
            !userModifiedNameRef.current &&
            jobDevices.length > 0 &&
            jobParts.length > 0 &&
            jobCycles.length > 0
        ) {
            const selectedDeviceObj = jobDevices.find(
                (d) => d.id === selectedDevice,
            );
            const selectedPartObj = jobParts.find(
                (p) => String(p.id) === String(selectedPart),
            );
            const selectedCycleObj = jobCycles.find(
                (c) => String(c.id) === String(selectedCycle),
            );
            const [start, end] = dateRange;

            let slugParts = [];

            if (selectedDeviceObj) {
                slugParts.push(selectedDeviceObj.id);
            }

            if (selectedPartObj) {
                slugParts.push(selectedPartObj.name);
            }

            if (selectedCycleObj) {
                slugParts.push(selectedCycleObj.name);
            }

            // Add date hash to ensure uniqueness
            if (start && end) {
                const dateHash = hashDates(start, end);
                if (dateHash) {
                    slugParts.push(dateHash);
                }
            }

            if (slugParts.length > 0) {
                const generatedSlug = slugify(slugParts.join(" "), {
                    lower: true,
                    strict: true,
                    replacement: "-",
                });

                // Only update if slug actually changed to avoid infinite loops
                if (generatedSlug !== previousSlugRef.current) {
                    previousSlugRef.current = generatedSlug;
                    setJobName(generatedSlug);
                }
            }
        }
    }, [
        selectedDevice,
        selectedPart,
        selectedCycle,
        dateRange,
        entity?.id,
        jobDevices,
        jobParts,
        jobCycles,
    ]);

    // Handle manual name changes
    const handleNameChange = (e) => {
        userModifiedNameRef.current = true;
        setJobName(e.target.value);
    };

    // Handle manual description changes
    const handleDescriptionChange = (e) => {
        userModifiedDescriptionRef.current = true;
        setJobDescription(e.target.value);
    };

    const fetchCycles = async () => {
        if (loadingCycles) return;
        setLoadingCycles(true);
        try {
            const query = {
                query: `
        {
          cycles(where: { plant_id: { equals: ${lastUsedPlant.id} } }) {
            id
            name
            plant_id
          }
        }
      `,
            };
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(query),
            });
            const result = await response.json();
            setJobCycles(result.data?.cycles || []);
        } catch (error) {
            console.error("Error fetching job cycles:", error);
            setJobCycles([]);
        } finally {
            setLoadingCycles(false);
        }
    };

    const fetchDevices = async () => {
        if (loadingDevices) return;
        setLoadingDevices(true);
        try {
            const query = {
                query: `
        {
          shadowDevices(where: { plant_id: { equals: ${lastUsedPlant.id} } }) {
            id
            name
            installed_at_zones {
              name
            }
          }
        }
      `,
            };
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(query),
            });
            const result = await response.json();
            setJobDevices(result.data?.shadowDevices || []);
        } catch (error) {
            console.error("Error fetching devices:", error);
            setJobDevices([]);
        } finally {
            setLoadingDevices(false);
        }
    };

    return (
        <SidebarView entityName="Job Schedule">
            <SidebarView.Page name="job_details" title="Job Details">
                <Form className="tw-flex tw-flex-col tw-gap-4">
                    <div>
                        <label className="tw-text-sm tw-font-medium">
                            Name <span className="tw-text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            className="tw-w-full tw-border tw-rounded-md tw-p-2"
                            value={jobName}
                            onChange={handleNameChange}
                            placeholder="Auto-generated unique name"
                        />
                    </div>

                    <div>
                        <label className="tw-text-sm tw-font-medium">
                            Select Date & Time Range{" "}
                            <span className="tw-text-red-500">*</span>
                        </label>
                        <div className="tw-overflow-visible tw-relative">
                            <DateRangePicker
                                format="yyyy-MM-dd HH:mm"
                                value={dateRange}
                                onChange={(range) => {
                                    if (range) {
                                        setDateRange(range);
                                    }
                                }}
                                block
                                placement="auto"
                                container={null}
                                popupClassName="rsuite-datepicker-override"
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="tw-text-sm tw-font-medium tw-flex tw-items-center tw-gap-2">
                            Select Device{" "}
                            <span className="tw-text-red-500">*</span>
                            {loadingDevices && (
                                <Spinner animation="border" size="sm" />
                            )}
                        </label>
                        <select
                            className="tw-w-full tw-border tw-rounded-md tw-p-2"
                            value={selectedDevice}
                            onChange={(e) => setSelectedDevice(e.target.value)}
                            disabled={loadingDevices}
                        >
                            <option value="">Select Device</option>
                            {jobDevices?.map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.id}(
                                    {d.installed_at_zones?.[0]?.name ||
                                        "No Zone"}
                                    )
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="tw-text-sm tw-font-medium tw-flex tw-items-center tw-gap-2">
                            Select Part{" "}
                            <span className="tw-text-red-500">*</span>
                            {loadingParts && (
                                <Spinner animation="border" size="sm" />
                            )}
                        </label>
                        <select
                            className="tw-w-full tw-border tw-rounded-md tw-p-2"
                            value={selectedPart}
                            onChange={(e) => setSelectedPart(e.target.value)}
                            disabled={loadingParts}
                        >
                            <option value="">Select Part</option>
                            {jobParts?.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="tw-text-sm tw-font-medium tw-flex tw-items-center tw-gap-2">
                            Select Cycle{" "}
                            <span className="tw-text-red-500">*</span>
                            {loadingCycles && (
                                <Spinner animation="border" size="sm" />
                            )}
                        </label>
                        <select
                            className="tw-w-full tw-border tw-rounded-md tw-p-2"
                            value={selectedCycle}
                            onChange={(e) => setSelectedCycle(e.target.value)}
                            disabled={loadingCycles}
                        >
                            <option value="">Select Cycle</option>
                            {jobCycles?.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="tw-text-sm tw-font-medium">
                            Description
                        </label>
                        <textarea
                            className="tw-w-full tw-border tw-rounded-md tw-p-2"
                            rows="4"
                            value={jobDescription}
                            onChange={handleDescriptionChange}
                            placeholder="Auto-generated description"
                        />
                    </div>
                </Form>
            </SidebarView.Page>
        </SidebarView>
    );
}

const JobScheduleSidebar = (props) => {
    const {
        show,
        onDiscard,
        item = null,
        plantId,
        onSave = async () => {},
        onDelete = async () => {},
    } = props;
    const starekContext = useContext(StarekContext);

    const handleSaveInternal = async (emittedData, entity) => {
        try {
            const jobName = emittedData.name?.trim();
            if (!jobName) {
                starekContext.showError("Job name is required");
                throw new Error("Job name is required");
            }
            if (!plantId) {
                starekContext.showError("Plant ID is required");
                throw new Error("Plant ID is required");
            }

            if (
                !emittedData.part ||
                !emittedData.cycle ||
                !emittedData.device
            ) {
                starekContext.showError(
                    "Please select Part, Cycle, and Device",
                );
                throw new Error("Please select Part, Cycle, and Device");
            }

            const [start, end] = emittedData.dateRange || [
                new Date(),
                new Date(),
            ];

            const payload = {
                id: entity?.id,
                name: jobName,
                description: emittedData.description || "",
                part_id: emittedData.part,
                cycle_id: emittedData.cycle,
                device_id: emittedData.device,
                start_time: start.toISOString(),
                end_time: end.toISOString(),
            };

            await onSave(payload);

            starekContext.showSuccess(
                entity?.id
                    ? `Job Schedule "${jobName}" updated successfully`
                    : `Job Schedule "${jobName}" created successfully`,
            );
        } catch (error) {
            console.error("Error saving job schedule:", error);
            // Don't show error again if we already showed it during validation
            if (
                !error.message.includes("select") &&
                !error.message.includes("required")
            ) {
                starekContext.showError(
                    error?.message ||
                        "Failed to save job schedule. Please try again.",
                );
            }
            throw error;
        }
    };

    return (
        <SidebarContextProvider
            entity={item}
            show={show}
            onSave={handleSaveInternal}
            onDelete={onDelete}
            onDiscard={onDiscard}
        >
            <JobScheduleSidebarContent />
        </SidebarContextProvider>
    );
};

export default JobScheduleSidebar;
